<template>
    <div v-cloak>
        <v-card id="courses" elevation="4" class="mb-8">
            <v-toolbar color="purple" dark flat dense>
                <v-toolbar-title>{{ enrolment.CourseName }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-scroll-to="'#top'" dark text>
                    <v-icon left>fas fa-chevron-up</v-icon>
                    Top
                </v-btn>
                <span v-if="IsEnrolmentRegistrationCompleted"
                      class="fa-stack fa-md icon-info">
                    <i class="fa fa-circle fa-stack-2x"></i>
                    <i class="fa fa-info fa-stack-1x"></i>
                </span>
                <span v-else
                      class="fa-stack fa-md icon-error">
                    <i class="fa fa-circle fa-stack-2x"></i>
                    <i class="fa fa-exclamation fa-stack-1x"></i>
                </span>
            </v-toolbar>
            <v-card-text>
                <p class="text-h6">Enrol on your course</p>
                <v-alert v-if="IsEnrolmentRegistrationCompleted" type="info" outlined dense text>You are enrolled on this course</v-alert>
                <v-alert v-if="IsEnrolmentRegistrationStarted" type="info" outlined dense text>You have begun to enrol on this course</v-alert>
                <v-alert v-if="IsEnrolmentRegistrationNotStarted" type="error" outlined dense text>You have not enrolled on this course yet</v-alert>

                <v-row>
                    <v-col>
                        <table border="0"
                               cellpadding="0"
                               cellspacing="5"
                               class="my-4">
                            <tr>
                                <th width="150" align="left">Course Code:</th>
                                <th align="left">{{ enrolment.CourseCode }}</th>
                            </tr>
                            <tr>
                                <td>Campus:</td>
                                <td>{{ enrolment.CampusName }}</td>
                            </tr>
                            <tr>
                                <td>Level:</td>
                                <td>{{ enrolment.SQAQualificationLevel }}</td>
                            </tr>
                            <tr>
                                <td>Mode:</td>
                                <td>{{ enrolment.IsFullTime ? 'Full Time' : 'Part Time' }}</td>
                            </tr>
                            <tr>
                                <td>Start Date:</td>
                                <td>{{ enrolment.StartDate | formatLongDate }}</td>
                            </tr>
                        </table>
                    </v-col>
                </v-row>

                <p v-if="!IsEnrolmentRegistrationCompleted"><strong>Please remember to sign in using your College Application Portal details (personal email address and password)</strong></p>
                <p>Please note; when completing your enrolment, you will be required to log in with your D&amp;A application credentials.  If you did not submit an application, you will be required to register.  It may take up to an hour after starting or completing your enrolment in the College Application Portal before this change in status shows here.</p>
                <p v-if="HasInductionEvents" class="mb-0">View your induction information and more information on your course:</p>
                <p v-else class="mb-0">View more information on your course:</p>
            </v-card-text>
            <v-card-actions class="pl-4">
                <v-row v-if="withdrawnApplication.applicationWithdrawn">
                    <v-col sm="auto" class="px-1">
                        <v-alert type="warning" outlined dense text>You have withdrawn your application for this course on {{withdrawnApplication.lastUpdated | formatDateTime}}.</v-alert>
                    </v-col>
                </v-row>
                <v-row v-else>
                    <v-col v-if="!IsEnrolmentRegistrationCompleted" sm="auto" class="px-1">
                        <v-btn :href="EnrolmentURI" target="_blank" color="error" dark small style="color:white !important">
                            <v-icon left small>fas fa-pencil-alt</v-icon>
                            Enrol now, via the Application Portal
                        </v-btn>
                    </v-col>
                    <v-col v-if="!IsEnrolmentRegistrationCompleted" sm="auto" class="px-1">
                        <v-btn @click="ConfirmApplicationWithdrawal" color="secondary" :loading="LoadingWithdrawnApplication" dark small style="color:white !important">
                            <v-icon left small>fas fa-times</v-icon>
                            Withdraw my Application
                        </v-btn>
                    </v-col>
                    <v-col v-if="loadingInductionEvents || HasInductionEvents(enrolment.CourseCode)" sm="auto" class="px-1">
                        <v-skeleton-loader v-if="loadingInductionEvents" type="button" class="mx-2"></v-skeleton-loader>
                        <v-btn v-if="HasInductionEvents(enrolment.CourseCode)" @click="OpenCourseInduction" color="info" dark small style="color:white !important">
                            <v-icon left small>far fa-calendar-alt</v-icon>
                            Your Induction
                        </v-btn>
                    </v-col>
                    <v-col sm="auto" class="px-1">
                        <v-btn @click="OpenCourseInformation" color="info" dark small style="color:white !important">
                            <v-icon left small>fas fa-book</v-icon>
                            Course and Staff Information
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>

        <confirm-dialog instance="withdrawn-application"
                        title="Confirm withdrawal of application"
                        question="Your application will now be withdrawn, do you wish to proceed?"
                        no-label="No"
                        yes-label="Yes"
                        button-color="error"></confirm-dialog>
    </div>
</template>

<script>
    import { eventBus } from '@/main'

    import filtersMixin from '@/views/components/mixins/base/filters.js'
    import appApiMixin from '@/views/components/mixins/appApi.js'
    import timetableEventsMixin from '@/views/components/mixins/enterpriseDataApi/timetableEvents.js'
    import withdrawnApplicationMixin from '@/views/components/mixins/withdrawnApplication.js'
    import studentsMixin from '@/views/components/mixins/enterpriseDataApi/students.js'

    import confirmDialog from '@/views/components/dialogs/ConfirmDialog.vue'

    export default {
        props: ['enrolment'],
        components: {
            'confirm-dialog': confirmDialog,
        },
        mixins: [
            filtersMixin,
            appApiMixin,
            timetableEventsMixin,
            withdrawnApplicationMixin,
            studentsMixin,
        ],
        data() {
            return {
                loadingWithdrawnApplication: false,
                submittedWithdrawnApplication: false,
            }
        },
        computed: {
            IsEnrolmentRegistrationNotStarted() {
                return ((this.enrolment.EnrolmentStatusCode == 'PREENR' && this.enrolment.EnrolmentStatus == 'Pre-registered - Enrolment Not Started') ||
                    this.enrolment.EnrolmentStatusCode == 'PEND');
            },
            IsEnrolmentRegistrationStarted() {
                return ['PREENR'].indexOf(this.enrolment.EnrolmentStatusCode) > -1 && ['Pre-registered - Enrolment Incomplete'].indexOf(this.enrolment.EnrolmentStatus) > -1;
            },
            IsEnrolmentRegistrationCompleted() {
                return ['ENR', 'CUR'].indexOf(this.enrolment.EnrolmentStatusCode) > -1;
            },
            EnrolmentURI() {
                switch (this.enrolment.EnrolmentStatusCode) {
                    case 'PEND':
                        return `https://apply.dundeeandangus.ac.uk/course-basket?course=${this.enrolment.CourseCode.substring(0, 6)}~${this.enrolment.CourseCode.substring(6, 12)}~${this.enrolment.CoursePeriodID}~${this.enrolment.AcademicSession}`;
                    default:
                        return 'https://apply.dundeeandangus.ac.uk/';
                }
            },
            LoadingWithdrawnApplication() {
                return this.loadingWithdrawnApplication || this.submittedWithdrawnApplication;
            }
        },
        mounted() {
            eventBus.$off(`withdrawn-application-fetch:${this.enrolment.EnrolmentID}:started`).$on(`withdrawn-application-fetch:${this.enrolment.EnrolmentID}:started`, () => this.loadingWithdrawnApplication = true);
            eventBus.$off(`withdrawn-application-fetch:${this.enrolment.EnrolmentID}:finished`).$on(`withdrawn-application-fetch:${this.enrolment.EnrolmentID}:finished`, () => this.loadingWithdrawnApplication = false);
            eventBus.$off(`withdrawn-application-save:${this.enrolment.EnrolmentID}:started`).$on(`withdrawn-application-save:${this.enrolment.EnrolmentID}:started`, () => this.submittedWithdrawnApplication = true);
            eventBus.$on(`withdrawn-application-save:${this.enrolment.EnrolmentID}:success`, response => {
                eventBus.$emit('alert:success', response);
                this.LoadWithdrawnApplication(this.enrolment.EnrolmentID);
            });
            eventBus.$off(`withdrawn-application-save:${this.enrolment.EnrolmentID}:finished`).$on(`withdrawn-application-save:${this.enrolment.EnrolmentID}:finished`, () => this.submittedWithdrawnApplication = false);

            this.LoadTimetableEvents(this.enrolment.CourseCode);
            this.LoadInductionEvents(this.enrolment.CourseCode);
            this.LoadWithdrawnApplication(this.enrolment.EnrolmentID);
            //this.LoadStudent();
        },
        methods: {
            OpenCourseInduction() {
                this.$router.push({ path: `/course/${this.enrolment.CourseCode}/induction` });
            },
            OpenCourseInformation() {
                this.$router.push({ path: `/course/${this.enrolment.CourseCode}/information` });
            },
            ConfirmApplicationWithdrawal() {
                eventBus.$off('withdrawn-application:ok').$on('withdrawn-application:ok', () => this.WithdrawApplication());
                eventBus.$emit('withdrawn-application:show');
            },
            WithdrawApplication() {
                var withdrawnApplication = {
                    enrolmentID: this.enrolment.EnrolmentID,
                    applicationWithdrawn: true,
                    Username: this.student.StudentReference,
                    Displayname: this.StudentDisplayName
                }
                this.SaveWithdrawnApplication(withdrawnApplication);
            }
        },
    }
</script>